/**
 * Owns the chat runtime and drives its lifecycle: connects on mount and
 * disconnects on unmount, but only for companies that have chat enabled. Mount
 * once inside the authenticated app shell so the connection (and the nav unread
 * badge) stays live regardless of which screen the user is on.
 *
 * The runtime is per-provider rather than a module singleton, so the store it
 * holds reaches the UI through context — see `useChat`.
 */
import { createContext, useEffect, useState, type ReactNode } from "react"
import { useCurrentCompanyContext } from "../components/AppShell/CurrentCompanyProvider"
import { RefusalToast } from "./components/RefusalToast"
import { createChatRuntime, type ChatRuntime } from "./runtime"

export const ChatRuntimeContext = createContext<ChatRuntime | null>(null)

export function ChatProvider({ children }: { children: ReactNode }) {
  const { currentCompany, isCurrentCompanyPending } = useCurrentCompanyContext()

  // Inert until started, so building one for a company without chat costs
  // nothing. StrictMode may build a second and throw it away — also inert.
  const [runtime] = useState(createChatRuntime)

  useEffect(() => {
    // Wait for the company to load before deciding — `chatEnabled` defaults to
    // false while the query is pending, so acting earlier would misreport.
    if (isCurrentCompanyPending) return

    // Capability gate: don't open a connection for companies without chat.
    // Logged either way — a feature that silently does nothing is impossible
    // to tell apart from a broken one.
    if (!currentCompany.chatEnabled) {
      console.info("[chat] not enabled for this company — not connecting")
      return
    }

    console.info("[chat] enabled for this company — connecting")
    runtime.start()
    return () => runtime.stop()
  }, [currentCompany.chatEnabled, isCurrentCompanyPending, runtime])

  return (
    <ChatRuntimeContext.Provider value={runtime}>
      {/* Above the screens rather than inside the chat view: a refusal can
          land after the reader has gone somewhere else, and it is still their
          message that did not send. */}
      <RefusalToast />
      {children}
    </ChatRuntimeContext.Provider>
  )
}
